import { useState, useMemo, useEffect } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, MessageCircle, AlertTriangle, Smartphone, Wifi } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AcaoConfig } from '@/hooks/useBKOAcao';

interface ItemPedido {
  id: string;
  pedido_id: string;
  coluna_atual?: string;
  quadro_atual?: string;
  produto_nome?: string;
  quantidade?: number;
  subtotal?: number;
  familia?: string;
  plano_nome?: string;
  bko_criacao_id?: string;
  bko_aceite_id?: string;
  bko_input_id?: string;
  bko_tramitacao_id?: string;
}

interface ConfirmarAcaoBKOModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemClicado: ItemPedido;
  todosItens: ItemPedido[];
  config: AcaoConfig;
  onConfirm: (itensSelecionados: string[]) => void;
  isLoading?: boolean;
}

export function ConfirmarAcaoBKOModal({
  open,
  onOpenChange,
  itemClicado,
  todosItens,
  config,
  onConfirm,
  isLoading = false,
}: ConfirmarAcaoBKOModalProps) {
  // Função robusta para extrair coluna (lida com Proxy objects, strings, etc.)
  const extractColuna = (val: any): string => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'string') return val.toUpperCase().trim();
    // Lidar com Proxy objects ou objetos com .value
    if (typeof val === 'object') {
      if ('value' in val) return String(val.value || '').toUpperCase().trim();
      try {
        const serialized = JSON.parse(JSON.stringify(val));
        if (typeof serialized === 'string') return serialized.toUpperCase().trim();
        if (typeof serialized === 'object' && 'value' in serialized) {
          return String(serialized.value || '').toUpperCase().trim();
        }
      } catch {}
    }
    return String(val || '').toUpperCase().trim();
  };

  // Filtrar itens na mesma coluna do item clicado
  const itensNaMesmaColuna = useMemo(() => {
    const colunaClicada = extractColuna(itemClicado.coluna_atual);
    
    console.log('🔍 [Modal] Filtrando itens:', {
      colunaClicada,
      totalItens: todosItens.length,
      colunasDisponiveis: todosItens.map(i => extractColuna(i.coluna_atual))
    });
    
    // Filtrar itens na mesma coluna
    let filtrados = todosItens.filter(item => {
      const colunaItem = extractColuna(item.coluna_atual);
      return colunaItem === colunaClicada;
    });
    
    // ✅ GARANTIA: Se nenhum item foi encontrado, incluir pelo menos o item clicado
    if (filtrados.length === 0) {
      console.warn('⚠️ [Modal] Nenhum item na mesma coluna encontrado, usando item clicado');
      filtrados = [itemClicado];
    }
    
    // ✅ GARANTIA: Verificar se o item clicado está na lista
    const temItemClicado = filtrados.some(item => item.id === itemClicado.id);
    if (!temItemClicado) {
      console.warn('⚠️ [Modal] Item clicado não estava na lista filtrada, adicionando');
      filtrados = [itemClicado, ...filtrados];
    }
    
    return filtrados;
  }, [todosItens, itemClicado]);

  // Estado de seleção (todos pré-selecionados por padrão)
  const [selecionados, setSelecionados] = useState<Set<string>>(new Set());

  // Inicializar seleção quando modal abrir
  useEffect(() => {
    if (open) {
      setSelecionados(new Set(itensNaMesmaColuna.map(item => item.id)));
    }
  }, [open, itensNaMesmaColuna]);

  // Toggle seleção de item
  const toggleItem = (itemId: string) => {
    setSelecionados(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        // Não permitir desmarcar o item clicado
        if (itemId === itemClicado.id) return prev;
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  // Contadores por família
  const contadorFamilia = useMemo(() => {
    const selecionadosArray = itensNaMesmaColuna.filter(i => selecionados.has(i.id));
    return {
      movel: selecionadosArray.filter(i => i.familia?.toUpperCase() === 'MÓVEL').length,
      fixa: selecionadosArray.filter(i => i.familia?.toUpperCase() === 'FIXA').length,
      total: selecionadosArray.length,
    };
  }, [itensNaMesmaColuna, selecionados]);

  // Seleção rápida por família
  const selecionarPorFamilia = (familia: 'MÓVEL' | 'FIXA' | 'TODOS') => {
    if (familia === 'TODOS') {
      setSelecionados(new Set(itensNaMesmaColuna.map(i => i.id)));
    } else {
      const itensFamilia = itensNaMesmaColuna.filter(i => i.familia?.toUpperCase() === familia);
      // Manter o item clicado sempre selecionado
      const novoSet = new Set([itemClicado.id, ...itensFamilia.map(i => i.id)]);
      setSelecionados(novoSet);
    }
  };

  const handleConfirm = () => {
    onConfirm(Array.from(selecionados));
  };

  // Identificar família do item
  const getFamiliaIcon = (familia?: string) => {
    const f = familia?.toUpperCase();
    if (f === 'MÓVEL') return <Smartphone className="w-3 h-3" />;
    if (f === 'FIXA') return <Wifi className="w-3 h-3" />;
    return null;
  };

  const getFamiliaBadge = (familia?: string) => {
    const f = familia?.toUpperCase();
    if (f === 'MÓVEL') {
      return (
        <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-primary/5 text-primary border-primary/40">
          <Smartphone className="w-2.5 h-2.5 mr-0.5" />
          MÓVEL
        </Badge>
      );
    }
    if (f === 'FIXA') {
      return (
        <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-muted text-primary border-border">
          <Wifi className="w-2.5 h-2.5 mr-0.5" />
          FIXA
        </Badge>
      );
    }
    return null;
  };

  const temMultiplasOpcoes = itensNaMesmaColuna.length > 1;
  const temWebhook = !!config.webhookKey;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <span>{config.icone}</span> {config.label}
          </AlertDialogTitle>
          {temWebhook && (
            <AlertDialogDescription className="flex items-start gap-2 p-3 bg-primary/5 dark:bg-primary/30 border border-primary/40 dark:border-primary rounded-lg text-primary dark:text-primary">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span className="text-sm">
                <strong>Esta ação envia mensagem WhatsApp ao cliente!</strong>
                {temMultiplasOpcoes && (
                  <span className="block mt-1 text-xs opacity-80">
                    Selecione os itens para processar juntos (1 mensagem consolidada).
                  </span>
                )}
              </span>
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>

        <div className="space-y-3 py-2">
          {/* Botões de seleção rápida */}
          {temMultiplasOpcoes && (
            <div className="flex flex-wrap gap-1.5">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => selecionarPorFamilia('TODOS')}
              >
                 Todos ({itensNaMesmaColuna.length})
              </Button>
              {contadorFamilia.movel > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs text-primary border-primary/40 hover:bg-primary/5"
                  onClick={() => selecionarPorFamilia('MÓVEL')}
                >
                  <Smartphone className="w-3 h-3 mr-1" />
                  Móveis ({itensNaMesmaColuna.filter(i => i.familia?.toUpperCase() === 'MÓVEL').length})
                </Button>
              )}
              {contadorFamilia.fixa > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs text-primary border-border hover:bg-muted"
                  onClick={() => selecionarPorFamilia('FIXA')}
                >
                  <Wifi className="w-3 h-3 mr-1" />
                  Fixos ({itensNaMesmaColuna.filter(i => i.familia?.toUpperCase() === 'FIXA').length})
                </Button>
              )}
            </div>
          )}

          {/* Lista de itens */}
          <div className="space-y-2 max-h-[200px] overflow-y-auto">
            {itensNaMesmaColuna.map(item => {
              const isItemClicado = item.id === itemClicado.id;
              return (
                <div
                  key={item.id}
                  className={cn(
                    "flex items-center gap-3 p-2 rounded-lg border transition-colors",
                    selecionados.has(item.id)
                      ? "bg-primary/5 border-primary/30"
                      : "bg-muted/30 border-transparent",
                    isItemClicado && "ring-2 ring-primary/50"
                  )}
                >
                  <Checkbox
                    checked={selecionados.has(item.id)}
                    onCheckedChange={() => toggleItem(item.id)}
                    disabled={isItemClicado} // Não pode desmarcar o item clicado
                    className="shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">
                        {item.produto_nome || 'Produto'}
                      </span>
                      {getFamiliaBadge(item.familia)}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{item.plano_nome || 'Sem plano'}</span>
                      <span>•</span>
                      <span>{item.quantidade || 1}x</span>
                      {item.subtotal && (
                        <>
                          <span>•</span>
                          <span>R$ {item.subtotal.toFixed(2)}</span>
                        </>
                      )}
                    </div>
                  </div>
                  {isItemClicado && (
                    <Badge variant="secondary" className="text-[10px] shrink-0">
                      clicado
                    </Badge>
                  )}
                </div>
              );
            })}
          </div>

          {/* Resumo */}
          {temWebhook && (
            <div className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2 text-sm">
                <MessageCircle className="w-4 h-4 text-success" />
                <span className="font-medium">{contadorFamilia.total} itens selecionados</span>
              </div>
              <Badge className="bg-success hover:bg-success">
                1 mensagem
              </Badge>
            </div>
          )}

          {!temWebhook && (
            <div className="p-2 bg-muted/50 rounded-lg text-sm text-center text-muted-foreground">
              {contadorFamilia.total} item(ns) serão processados
            </div>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isLoading || selecionados.size === 0}
            className={cn(config.corClasse, "text-white")}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processando...
              </>
            ) : (
              <>
                {config.icone} Processar {selecionados.size} Item(ns)
              </>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
